<script setup>
/**
 * AppToaster - Pile de notifications toast modernes.
 *
 * À monter une seule fois dans App.vue.
 * Couleurs alignées sur la charte (or / vert / orange / rouge) avec
 * animations d'entrée/sortie en fluide.
 *
 * Empilement vertical en haut à droite avec un z-index supérieur à tout
 * (dialogs / sidebar / overlays).
 */
import { useNotifier } from '@/composables/useNotifier'

const { toasts, dismiss } = useNotifier()

const TYPE_ICON = {
  success: 'pi-check-circle',
  error: 'pi-times-circle',
  warning: 'pi-exclamation-triangle',
  info: 'pi-info-circle',
}

const DEFAULT_TITLE = {
  success: 'Succès',
  error: 'Erreur',
  warning: 'Attention',
  info: 'Information',
}
</script>

<template>
  <Teleport to="body">
    <div class="toaster" role="region" aria-live="polite" aria-label="Notifications">
      <TransitionGroup name="toast">
        <article
          v-for="t in toasts"
          :key="t.id"
          class="toast"
          :class="`toast--${t.type}`"
          role="status"
        >
          <span class="toast-icon" :class="`toast-icon--${t.type}`">
            <i :class="['pi', TYPE_ICON[t.type] || 'pi-info-circle']"></i>
          </span>

          <div class="toast-body">
            <h4 class="toast-title">{{ t.title || DEFAULT_TITLE[t.type] }}</h4>
            <p v-if="t.message" class="toast-message">{{ t.message }}</p>
            <button
              v-if="t.action"
              type="button"
              class="toast-action"
              @click="(t.action.onClick?.(), dismiss(t.id))"
            >
              {{ t.action.label }}
            </button>
          </div>

          <button
            v-if="t.closable"
            type="button"
            class="toast-close"
            aria-label="Fermer la notification"
            @click="dismiss(t.id)"
          >
            <i class="pi pi-times"></i>
          </button>

          <span
            v-if="t.duration > 0"
            class="toast-progress"
            :style="{ animationDuration: `${t.duration}ms` }"
          ></span>
        </article>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toaster {
  position: fixed;
  top: var(--space-4, 16px);
  right: var(--space-4, 16px);
  display: flex;
  flex-direction: column;
  gap: var(--space-2, 8px);
  z-index: 9999;
  pointer-events: none;
  width: min(380px, calc(100vw - 32px));
}

.toast {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: flex-start;
  gap: var(--space-3, 12px);
  background: white;
  color: var(--text-primary, #212121);
  border: 1px solid var(--tertiary-200, #e5e0d3);
  border-left-width: 4px;
  border-radius: var(--border-radius-lg, 12px);
  padding: var(--space-3, 12px) var(--space-4, 16px);
  box-shadow: 0 12px 32px -12px rgba(0, 0, 0, 0.18), 0 4px 12px -4px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  pointer-events: auto;
}

.toast--success { border-left-color: #16a34a; }
.toast--error { border-left-color: #dc2626; }
.toast--warning { border-left-color: #d97706; }
.toast--info { border-left-color: var(--primary-500, #C3A363); }

.toast-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
}

.toast-icon--success { background: #dcfce7; color: #16a34a; }
.toast-icon--error { background: #fee2e2; color: #dc2626; }
.toast-icon--warning { background: #fef3c7; color: #d97706; }
.toast-icon--info { background: var(--primary-100, #eee5cf); color: var(--primary-700, #8b6e3b); }

.toast-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.toast-title {
  margin: 0;
  font-size: var(--text-sm, 14px);
  font-weight: var(--font-bold, 700);
  color: var(--text-primary, #212121);
  line-height: 1.3;
}

.toast-message {
  margin: 0;
  font-size: var(--text-xs, 12px);
  color: var(--text-secondary, #555);
  line-height: 1.4;
  word-wrap: break-word;
}

.toast-action {
  margin-top: 6px;
  background: none;
  border: none;
  padding: 0;
  color: var(--primary-700, #8b6e3b);
  font-size: var(--text-xs, 12px);
  font-weight: var(--font-semibold, 600);
  cursor: pointer;
  text-decoration: underline;
  align-self: flex-start;
}

.toast-action:hover { opacity: 0.8; }

.toast-close {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted, #999);
  font-size: 11px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 4px;
  transition: all 0.15s ease;
}

.toast-close:hover {
  background: var(--tertiary-100, #f4f0e6);
  color: var(--text-primary, #212121);
}

/* Barre de progression */
.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: currentColor;
  opacity: 0.35;
  width: 100%;
  transform-origin: left center;
  animation-name: toast-progress;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

.toast--success .toast-progress { color: #16a34a; }
.toast--error .toast-progress { color: #dc2626; }
.toast--warning .toast-progress { color: #d97706; }
.toast--info .toast-progress { color: var(--primary-500, #C3A363); }

@keyframes toast-progress {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}

/* Animations entrée / sortie */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.21, 1.02, 0.73, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(40px) scale(0.96);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(40px) scale(0.94);
}

.toast-leave-active {
  position: absolute;
  width: 100%;
}
</style>
